import { useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import { DocumentView } from "@/components/documents/DocumentView";
import { createUnavailableDocumentResource } from "@/lib/document-resource";
import { Button } from "@/components/design/Button";
import { ingestFiles } from "@/lib/file-ingest";
import { logApplicationFailure } from "@/lib/user-error";
import { toUploadedFile, type UploadedFile } from "./uploaded-file";

// Like Save and Match: a dashed empty field, then tiles as the overlay drop target with an upload button
// (@adr SPARK-0022-file-drop-fields@4). Checks size and type before reading a file; what happens with the list is
// the caller's. Used for a post's images and attachments (ShareCreatePage) and a listing's images.
export function FileDrop({
  idPrefix,
  label,
  accept,
  items,
  onChange,
  onAppend,
  headerAction,
  maxBytes,
  onSelectMain,
  hint,
}: {
  /** Testanker; unbesetzt, wo kein Test hingreift. */
  idPrefix?: string;
  label: string;
  accept: string;
  items: UploadedFile[];
  onChange: (next: UploadedFile[]) => void;
  onAppend: (next: UploadedFile[]) => void;
  headerAction?: ReactNode;
  maxBytes: number;
  /** Image grids only: click marks a tile as the item's mainImage. */
  onSelectMain?: (index: number) => void;
  /** Empty zone only: the line under the prompt (formats, size limit). */
  hint?: ReactNode;
}) {
  const intl = useIntl();
  const zoneRef = useRef<FileDropZoneHandle>(null);
  const [busy, setBusy] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);

  async function ingest(fileList: FileList | null) {
    setBusy(true);
    try {
      const result = await ingestFiles(fileList, { maxBytes, accept });
      const unsupported = result.rejected.filter((r) => r.reason === "unsupportedType");
      const tooLarge = result.rejected.filter((r) => r.reason === "tooLarge");
      if (unsupported.length > 0) {
        setLimitError(
          intl.formatMessage(
            {
              id: "share.create.unsupportedType",
              defaultMessage: "Unsupported file type: {names}",
            },
            { names: unsupported.map((r) => r.file.name).join(", ") },
          ),
        );
      } else if (tooLarge.length > 0) {
        setLimitError(
          intl.formatMessage(
            {
              id: "share.create.tooLarge",
              defaultMessage: "Too large (max. {max} MB per file): {names}",
            },
            {
              max: Math.floor(maxBytes / (1024 * 1024)),
              names: tooLarge.map((r) => r.file.name).join(", "),
            },
          ),
        );
      } else {
        setLimitError(null);
      }
      if (result.accepted.length === 0) return;
      const accepted = result.accepted.map(toUploadedFile);
      onAppend(accepted);
    } catch {
      logApplicationFailure("share.file-drop-read");
    } finally {
      setBusy(false);
    }
  }

  function remove(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div id={idPrefix && `${idPrefix}-count`} className="text-smycles-micro tracking-[0.25em] text-ink-muted">
          {label} · {items.length}
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 ? (
            <Button
              id={idPrefix && `${idPrefix}-upload`}
              variant="secondary"
              size="compact"
              onClick={() => zoneRef.current?.openPicker()}
            >
              <FormattedMessage id="service.page.uploadFile" defaultMessage="Upload file" />
            </Button>
          ) : null}
          {headerAction}
        </div>
      </div>

      <FileDropZone
        ref={zoneRef}
        id={idPrefix}
        inputId={idPrefix && `${idPrefix}-file`}
        variant={items.length === 0 ? "zone" : "overlay"}
        accept={accept}
        ariaLabel={label}
        className="mt-2 rounded-md"
        clickToOpen={items.length === 0}
        onFiles={(fl) => void ingest(fl)}
        label={
          busy ? (
            <FormattedMessage id="share.create.loadingFiles" defaultMessage="loading…" />
          ) : undefined
        }
        hint={hint}
      >
        {items.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 @sm:grid-cols-3 @lg:grid-cols-4">
            {items.map((f, i) => (
              <DocumentView variant="tile"
                file={f.document ?? (f.content ? f : createUnavailableDocumentResource({ name: f.name, mimeType: f.mimeType, reason: "original-missing" }))}
                key={i}
                idPrefix={idPrefix && `${idPrefix}-${f.name}`}
                name={f.name}
                previewUrl={f.preview || null}
                selected={f.isMain}
                badge={
                  f.isMain ? (
                    <span className="rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
                      <FormattedMessage
                        id="share.create.mainImageBadge"
                        defaultMessage="Preview image"
                      />
                    </span>
                  ) : undefined
                }
                onClick={onSelectMain ? () => onSelectMain(i) : undefined}
                title={
                  onSelectMain
                    ? intl.formatMessage({
                        id: "share.create.mainImageHint",
                        defaultMessage: "Set as preview image",
                      })
                    : undefined
                }
                onRemove={() => remove(i)}
              />
            ))}
          </div>
        ) : null}
      </FileDropZone>
      {limitError ? <div id={idPrefix && `${idPrefix}-error`} className="mt-1 text-smycles-meta text-ink-accent">{limitError}</div> : null}
    </div>
  );
}
